import {isDevToolsEnabled} from "@bounce/shared";
import {
  BounceRole,
  BundlePreLoginContext,
  PartnerDomain,
  PreLoginContext,
  UserContextDetails,
} from "@bounce/shared/consumer/declarations";
import {FeatureFlags} from "@bounce/shared/consumer/helpers";
import {useAuth} from "@bounce/shared/contexts";
import {useLocalStorageState} from "@bounce/shared/hooks";
import {LoadingCenter} from "@bounce/shared/ui";
import React, {createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {useLocation} from "react-router-dom";
import {useGetConsumerContext, useGetPartnerNoLoginContext} from "~/api/requests";
import {useLoginDestinationUrl} from "~/contexts/LoginDestinationContext";
import {applyOverrides} from "~/dev-tools/feature-flags/featureFlagOverrides";
import {isPreLoginRole} from "~/helpers/roles";
import {useRedirectNoActivePlan} from "~/hooks/useRedirectNoActivePlan";
import {removeTokenFromUrl} from "./user-context-utils";

export interface ConsumerUserContextProps {
  clearPreLoginInfo: () => void;
  isMultiAccount?: boolean;
  setIsMultiAccount: (value: boolean) => void;
  userContext?: UserContextDetails;
  preLoginContext?: PreLoginContext;
  bundleContext?: BundlePreLoginContext;
  isLoadingUserContext: boolean;
  isNoLogin?: boolean;
  preLoginToken?: string;
  featureFlags?: FeatureFlags;
  applyDevOverrides: () => void;
}

export interface IPreLoginInfo {
  preLoginToken?: string;
  userId?: string;
  linkId?: string;
  partner?: string;
  externalId?: string;
}

const ConsumerUserContext = createContext<ConsumerUserContextProps>(undefined!);

export const ConsumerUserProvider = ({children}: PropsWithChildren) => {
  const {token, setToken, userRole, userId, setUserId, credentials, tokenExpired, isAnonymous, partnerDomain} =
    useAuth();
  const {search, state} = useLocation();
  const {clearDestinationUrl} = useLoginDestinationUrl();

  const queryParams = new URLSearchParams(search);
  const urlUserId = queryParams.get("id") || state?.userId || undefined;
  const [isMultiAccount, setIsMultiAccount] = useState<boolean>();
  const [userContext, setUserContext] = useState<UserContextDetails | undefined>();
  const [preLoginContext, setPreLoginContext] = useState<PreLoginContext | undefined>();
  const [bundleContext, setBundleContext] = useState<BundlePreLoginContext | undefined>();
  const [preLoginInfo, setPreLoginInfo] = useLocalStorageState<IPreLoginInfo | undefined>("preLoginInfo", undefined, {
    encrypted: true,
  });
  const linkId = queryParams.get("lid") || preLoginInfo?.linkId || undefined;
  const partner = queryParams.get("partner") || preLoginInfo?.partner || undefined;
  const externalId = queryParams.get("external-id") || preLoginInfo?.externalId || undefined;
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>();
  const [isNoLoginTokenApplied, setIsNoLoginTokenApplied] = useState(false);
  const storedToken = credentials?.token || preLoginInfo?.preLoginToken;
  const [isStoredTokenApplied, setIsStoredTokenApplied] = useState(!storedToken);

  const enableConsumerContext = useMemo(() => {
    return !externalId && (!!userId || !!urlUserId || !!preLoginInfo?.userId);
  }, [externalId, userId, urlUserId, preLoginInfo]);

  const {data: consumerContextDetails, isInitialLoading: isLoadingConsumerContext} = useGetConsumerContext(
    {userId: userId || urlUserId || preLoginInfo?.userId, linkId: linkId},
    {enabled: enableConsumerContext}
  );

  const {data: partnerNoLoginContextDetails, isInitialLoading: isLoadingPartnerNoLoginContext} =
    useGetPartnerNoLoginContext({partner, externalId}, {enabled: !!externalId});

  const userContextDetails = consumerContextDetails || partnerNoLoginContextDetails;
  const isLoadingUserContext = isLoadingConsumerContext || isLoadingPartnerNoLoginContext;

  useEffect(() => {
    setUserContext(userContextDetails);
    if (userContextDetails?.pre_login_context) {
      setPreLoginContext(userContextDetails.pre_login_context);
    }
    if (userContextDetails?.pre_login_context?.bundle_context) {
      setBundleContext(userContextDetails.pre_login_context.bundle_context);
    }
  }, [userContextDetails]);

  useEffect(() => {
    if (isPreLoginRole(userRole) && !userId) {
      setUserId(urlUserId || preLoginInfo?.userId);
    }
  }, [userRole, userId, preLoginInfo]);

  useEffect(() => {
    if (userContext?.feature_flags) {
      setFeatureFlags(userContext.feature_flags);
    }
  }, [userContext]);

  const applyDevOverrides = useCallback(() => {
    if (!isDevToolsEnabled() || !userContextDetails?.feature_flags) return;
    const flags = applyOverrides(userContextDetails.feature_flags);
    if (flags) {
      setFeatureFlags(flags);
      setUserContext({...userContextDetails, feature_flags: flags});
    }
  }, [userContextDetails]);

  useEffect(() => {
    applyDevOverrides();
  }, [applyDevOverrides]);

  const clearPreLoginInfo = useCallback((): void => {
    setPreLoginInfo(undefined);
  }, [setPreLoginInfo]);

  const isNoLogin = !!preLoginContext && userRole === BounceRole.NO_LOGIN_USER;

  const isRedirectingToHappyMoney = useRedirectNoActivePlan(
    !isAnonymous && partnerDomain === PartnerDomain.HAPPY_MONEY,
    userContext
  );

  useEffect(() => {
    const queryToken = queryParams.get("token");
    if (queryToken && !credentials?.token) {
      setPreLoginInfo({preLoginToken: queryToken, userId: urlUserId, linkId: linkId});
      setToken(queryToken);
    } else {
      setToken(credentials?.token || preLoginInfo?.preLoginToken || undefined);
    }
  }, [credentials]);

  useEffect(() => {
    if (!partnerNoLoginContextDetails?.token) return;
    setPreLoginInfo({
      preLoginToken: partnerNoLoginContextDetails.token,
      userId: partnerNoLoginContextDetails.public_id,
      linkId: linkId,
      partner: partner,
      externalId: externalId,
    });
    setToken(partnerNoLoginContextDetails.token);
  }, [partnerNoLoginContextDetails]);

  useEffect(() => {
    if (!!token && token === partnerNoLoginContextDetails?.token) {
      setIsNoLoginTokenApplied(true);
    }
  }, [token, partnerNoLoginContextDetails]);

  useEffect(() => {
    if (token === storedToken) {
      setIsStoredTokenApplied(true);
    }
  }, [token, storedToken]);

  useEffect(() => {
    if (!tokenExpired) return;
    setToken(undefined);
    setPreLoginInfo(undefined);
    clearDestinationUrl();
  }, [tokenExpired]);

  useEffect(() => {
    removeTokenFromUrl();
  }, [token]);

  const isWaitingForNoLoginToken = !!partnerNoLoginContextDetails && !isNoLoginTokenApplied;

  if (
    ((isLoadingUserContext || isRedirectingToHappyMoney) && !isAnonymous) ||
    isLoadingPartnerNoLoginContext ||
    isWaitingForNoLoginToken ||
    !isStoredTokenApplied
  ) {
    return <LoadingCenter />;
  }

  return (
    <ConsumerUserContext.Provider
      value={{
        isMultiAccount,
        setIsMultiAccount,
        clearPreLoginInfo,
        userContext,
        preLoginContext,
        bundleContext,
        isLoadingUserContext,
        isNoLogin,
        preLoginToken: preLoginInfo?.preLoginToken,
        featureFlags,
        applyDevOverrides,
      }}
    >
      {children}
    </ConsumerUserContext.Provider>
  );
};

export const useConsumerUser = (): ConsumerUserContextProps => {
  const context = useContext(ConsumerUserContext);
  if (!context) throw new Error("ConsumerUserContext must be used inside ConsumerUserProvider");
  return context;
};

export {ConsumerUserContext};
